const DEFAULT_TRANCHE_WINDOW_MS = 5 * 60 * 1000

function upper(value) {
  return String(value ?? '').toUpperCase()
}

function normalizeSide(value) {
  return upper(value)
}

function executionTimeMs(exec) {
  const ms = new Date(exec?.executed_at ?? exec?.timestamp ?? 0).getTime()
  return Number.isFinite(ms) ? ms : 0
}

function executionChildren(exec) {
  return Array.isArray(exec?._children) && exec._children.length > 0
    ? exec._children
    : [exec]
}

function executionIdentity(exec) {
  const children = executionChildren(exec)
  return children
    .map(child => [
      child?.id,
      child?.trade_id,
      child?.account_number,
      child?.executed_at,
      child?.price,
      child?.quantity,
    ].join('|'))
    .join('::')
}

function isBuyExecution(exec) {
  const side = normalizeSide(exec?.side)
  return side === 'BUY' || side === 'BUY_TO_OPEN'
}

function stopPriceKey(value) {
  const price = Number(value)
  if (!Number.isFinite(price)) return null
  return price.toFixed(2)
}

function stopGroups(stopOrders) {
  const groups = new Map()
  for (const stop of Array.isArray(stopOrders) ? stopOrders : []) {
    const key = stopPriceKey(stop?.stopPrice ?? stop?.price)
    if (!key) continue
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(stop)
  }
  return groups
}

export function groupBuyExecutionsIntoTranches({
  executions = [],
  symbol,
  trancheWindowMs = DEFAULT_TRANCHE_WINDOW_MS,
} = {}) {
  const upperSymbol = upper(symbol)
  const buys = (Array.isArray(executions) ? executions : [])
    .filter(exec => isBuyExecution(exec))
    .filter(exec => !upperSymbol || upper(exec?.symbol) === upperSymbol)
    .sort((a, b) => executionTimeMs(a) - executionTimeMs(b))

  if (buys.length === 0) return []

  const tranches = []
  let current = [buys[0]]
  for (let i = 1; i < buys.length; i += 1) {
    const gap = executionTimeMs(buys[i]) - executionTimeMs(buys[i - 1])
    if (gap >= 0 && gap <= trancheWindowMs) {
      current.push(buys[i])
    } else {
      tranches.push(current)
      current = [buys[i]]
    }
  }
  tranches.push(current)
  return tranches
}

export function matchStopGroupsToBuyTranches({ buyTranches = [], stopOrders = [] } = {}) {
  const groups = stopGroups(stopOrders)
  const stopKeys = [...groups.keys()].sort((a, b) => Number(a) - Number(b))
  const map = new Map()
  const matchCount = Math.min(buyTranches.length, stopKeys.length)
  if (matchCount === 0) return map

  for (let i = 0; i < matchCount; i += 1) {
    map.set(stopKeys[i], buyTranches[i])
  }
  return map
}

function matchedStopFromTranches({ execution, executions, stopOrders, symbol }) {
  const targetId = executionIdentity(execution)
  if (!targetId) return null

  const tranches = groupBuyExecutionsIntoTranches({ executions, symbol })
  const matched = matchStopGroupsToBuyTranches({ buyTranches: tranches, stopOrders })
  for (const [key, trancheExecs] of matched) {
    if (trancheExecs.some(exec => executionIdentity(exec) === targetId)) {
      return Number(key)
    }
  }
  return null
}

function fallbackMatchedStop(execution, stopOrders) {
  if (!execution?.price) return null
  const buyPrice = Number(execution.price)
  if (!Number.isFinite(buyPrice)) return null

  const stops = (Array.isArray(stopOrders) ? stopOrders : [])
    .map(stop => Number(stop?.stopPrice ?? stop?.price))
    .filter(price => Number.isFinite(price))
  if (stops.length === 0) return null

  let bestStop = null
  let bestDist = Infinity
  for (const price of stops) {
    if (price <= buyPrice) {
      const dist = buyPrice - price
      if (dist < bestDist) {
        bestDist = dist
        bestStop = price
      }
    }
  }
  if (bestStop !== null) return bestStop

  for (const price of stops) {
    const dist = Math.abs(buyPrice - price)
    if (dist < bestDist) {
      bestDist = dist
      bestStop = price
    }
  }
  return bestStop
}

export function getMatchedStopPriceForExecution({
  execution,
  executions = [],
  stopOrders = [],
  symbol,
} = {}) {
  const matched = matchedStopFromTranches({ execution, executions, stopOrders, symbol })
  if (matched != null) return matched
  return fallbackMatchedStop(execution, stopOrders)
}

export function executionMatchesStopPrice({ execution, stopPrice, executions, stopOrders, symbol } = {}) {
  const matched = getMatchedStopPriceForExecution({ execution, executions, stopOrders, symbol })
  return matched != null && Math.abs(matched - Number(stopPrice)) < 0.01
}
